import { analyzeAnswers } from './analytics';
import { RuleProvider } from './diagnosis';
import { generateFollowUpQuestions } from './follow-up';
import { generateYearTest, questionText } from './question';
import { scoreAnswer } from './scoring';
import { getYearConfig, type YearKey } from './year-config';
import type { DiagnosisProvider } from './provider';
import type { CompletedAnswer, Question, SessionType, WeakCategory } from './types';

export const FOLLOWUP_QUESTION_COUNT = 15;

export interface LiveSession {
  type: SessionType;
  year: YearKey;
  questions: Question[];
}

export interface SlowestEntry {
  position: number;
  question: string;
  givenAnswer: number | null;
  correctAnswer: number;
  timeMs: number | null;
  isCorrect: boolean;
}

export interface SessionResult {
  type: SessionType;
  year: YearKey;
  yearLabel: string;
  score: number;
  questionCount: number;
  totalTimeMs: number;
  answers: CompletedAnswer[];
  slowest: SlowestEntry[];
  weakCategories: WeakCategory[];
  diagnosis: string;
  focusArea: string;
}

export interface StartTestOptions {
  /** Slider override: 0..1 share of additions (ignored for single-type years). */
  additionRatio?: number;
  random?: () => number;
}

export function startTest(year: YearKey, options: StartTestOptions = {}): LiveSession {
  const config = getYearConfig(year);
  return {
    type: 'test',
    year,
    questions: generateYearTest(config, { additionRatio: options.additionRatio, random: options.random })
  };
}

export function startFollowUp(previous: SessionResult, random?: () => number): LiveSession {
  return {
    type: 'followup',
    year: previous.year,
    questions: generateFollowUpQuestions({
      answers: previous.answers,
      weakCategories: previous.weakCategories,
      questionCount: FOLLOWUP_QUESTION_COUNT,
      yearConfig: getYearConfig(previous.year),
      random
    })
  };
}

export function recordAnswer(
  question: Question,
  givenAnswer: number | null,
  timeMs: number
): CompletedAnswer {
  const safeTimeMs = Number.isFinite(timeMs) && timeMs >= 0 ? Math.round(timeMs) : 0;
  return {
    ...question,
    givenAnswer,
    timeMs: safeTimeMs,
    isCorrect: scoreAnswer(question.correctAnswer, givenAnswer)
  };
}

export async function completeSession(
  session: LiveSession,
  answers: CompletedAnswer[],
  provider: DiagnosisProvider = new RuleProvider()
): Promise<SessionResult> {
  const config = getYearConfig(session.year);
  // Reconcile against the planned questions so an incomplete run scores out of
  // the full count and unanswered questions count as missed.
  const answersByPosition = new Map(answers.map((answer) => [answer.position, answer]));
  const plannedAnswers: CompletedAnswer[] = session.questions.map((question) =>
    answersByPosition.get(question.position) ?? {
      ...question,
      givenAnswer: null,
      timeMs: null,
      isCorrect: false
    }
  );

  const analysis = analyzeAnswers(plannedAnswers);
  const score = analysis.answers.filter((answer) => answer.isCorrect).length;
  const totalTimeMs = analysis.answers.reduce((sum, answer) => sum + (answer.timeMs ?? 0), 0);
  const diagnosis = await provider.diagnose({
    yearLabel: config.label,
    score,
    questionCount: session.questions.length,
    totalTimeMs,
    weakCategories: analysis.weakCategories.slice(0, 3),
    answers: analysis.answers
  });

  return {
    type: session.type,
    year: session.year,
    yearLabel: config.label,
    score,
    questionCount: session.questions.length,
    totalTimeMs,
    answers: analysis.answers,
    slowest: analysis.slowest.map((answer) => ({
      position: answer.position,
      question: questionText(answer),
      givenAnswer: answer.givenAnswer,
      correctAnswer: answer.correctAnswer,
      timeMs: answer.timeMs,
      isCorrect: answer.isCorrect
    })),
    weakCategories: analysis.weakCategories,
    diagnosis: diagnosis.diagnosis,
    focusArea: diagnosis.focusArea
  };
}
